import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { isLongevityUnlocked } from "@/lib/LongevityEngine";
import { useAuth } from "@/lib/auth";
import BiometricVault from "@/components/longevity/BiometricVault";
import HealthScreenshotCard from "@/components/longevity/HealthScreenshotCard";
import LongevityOverview from "@/components/longevity/report/LongevityOverview";
import DetailedResults from "@/components/longevity/report/DetailedResults";
import { AREA_META, prettyDate } from "@/components/longevity/report/reportMeta";
import { downloadLongevityReport } from "@/components/longevity/report/longevityPdf";

/**
 * Longevity Dashboard — Guest Biometric Vault (Feb 2026, redesigned Jun 2026).
 *
 * Route: /portal/longevity
 * Access: Longevity whitelist for guests (staff bypass separately).
 *
 * Composition:
 *  - Report header (logo · title · guest info box) + Download Report.
 *  - Section 1 "Your Longevity Overview" — score, age profile, 7 diagnostic
 *    areas, progress over time, key improvements, next focus.
 *  - Section 2 "Your Detailed Results" — one card per diagnostic area.
 *  - The original Biometric Vault, unchanged — tapping a card still opens
 *    the same data-entry flow. The dashboard is a read-only derived view
 *    (`GET /longevity/me/report`); nothing about storage or input changed.
 */
export default function LongevityDashboard() {
  const { customer } = useAuth();
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const unlocked = isLongevityUnlocked(customer?.email);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/longevity/me/biometric");
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try {
      const r = await customerApi.get("/longevity/me/report");
      setReport(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadOnboarding = useCallback(async () => {
    try {
      const r = await customerApi.get("/longevity/me/onboarding");
      setOnboarding(r.data?.record || null);
    } catch (_) {
      /* silent — the CTA simply falls back to "Start onboarding" */
    }
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    loadEntries();
    loadReport();
    loadOnboarding();
  }, [unlocked, loadEntries, loadReport, loadOnboarding]);

  // A HOWBODY or MEICET upload lands in its own collection, so the report
  // (which reads Cocoon Biometric 360 and Skin Analysis off those sheets) has
  // to repaint after a save.
  useEffect(() => {
    const onSaved = () => loadReport();
    window.addEventListener("cocoon:body-composition-saved", onSaved);
    window.addEventListener("cocoon:skin-analysis-saved", onSaved);
    return () => {
      window.removeEventListener("cocoon:body-composition-saved", onSaved);
      window.removeEventListener("cocoon:skin-analysis-saved", onSaved);
    };
  }, [loadReport]);

  // Every save appends a new record — refresh both the raw timeline and the
  // derived report so the dashboard repaints live.
  const handleSave = useCallback(
    async (payload) => {
      try {
        await customerApi.post("/longevity/me/biometric", payload);
        await Promise.all([loadEntries(), loadReport()]);
      } catch (e) {
        throw new Error(formatApiError(e));
      }
    },
    [loadEntries, loadReport],
  );

  // Tints live on the frontend; the PDF needs them alongside the scores.
  const pdfReport = useMemo(() => {
    if (!report) return null;
    return {
      ...report,
      areas: (report.areas || []).map((a) => ({ ...a, tint_hex: AREA_META[a.key]?.tint })),
    };
  }, [report]);

  const handleDownload = useCallback(async () => {
    if (!pdfReport) return;
    setDownloading(true);
    try {
      await downloadLongevityReport(pdfReport);
      toast.success("Your longevity report has been downloaded.");
    } catch (e) {
      toast.error(e?.message || "Could not generate the PDF.");
    } finally {
      setDownloading(false);
    }
  }, [pdfReport]);

  if (!unlocked) return <Navigate to="/portal/profile" replace />;

  const today = prettyDate(new Date().toISOString().slice(0, 10));

  return (
    <div>
      <CustomerNav />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        {/* Report header */}
        <Card className="rounded-2xl mb-5" data-testid="longevity-report-header">
          <CardContent className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <img
              src="/brand/cocoon-logo.png"
              alt="Cocoon Longevity Resort Bali"
              className="h-12 w-auto self-start lg:self-center"
              data-testid="longevity-report-logo"
            />
            <div className="hidden lg:block w-px h-12 bg-border" />
            <div className="flex-1 text-center lg:text-left">
              <p className="tracking-overline text-[11px] text-muted-foreground">YOUR</p>
              <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl leading-none" data-testid="longevity-dashboard-heading">
                Longevity Overview
              </h1>
              <p className="text-sm text-muted-foreground mt-1">A Healthier, Younger You</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs" data-testid="longevity-guest-info-box">
                <div className="grid grid-cols-[52px_1fr] gap-y-1">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium" data-testid="longevity-guest-name">{report?.guest?.name || "—"}</span>
                  <span className="text-muted-foreground">Age</span>
                  <span className="font-medium" data-testid="longevity-guest-age">{report?.guest?.age ?? "—"}</span>
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium" data-testid="longevity-report-date">{today}</span>
                </div>
              </div>
              <Button
                onClick={handleDownload}
                disabled={!report || downloading}
                className="rounded-full"
                data-testid="longevity-download-report-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                {downloading ? "Preparing…" : "Download Report"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upload Health Report — the single upload entry point for every report type */}
        <HealthScreenshotCard onSave={handleSave} />

        {/* Section 1 */}
        {report ? (
          <LongevityOverview report={report} />
        ) : (
          <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="longevity-report-loading">
            Building your longevity overview…
          </CardContent></Card>
        )}

        {/* Section 2 */}
        {report && (
          <div className="mt-8">
            <DetailedResults report={report} />
          </div>
        )}

        {/* Onboarding CTA — also the Lifestyle Questionnaire data-entry surface */}
        <Card
          className="rounded-2xl mt-8 mb-6 border-primary/20 bg-primary/5 transition-all"
          data-testid="longevity-onboarding-cta-card"
        >
          <CardContent className="p-5 sm:p-6 flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary/10 text-primary flex-shrink-0">
              {onboarding?.status === "submitted" ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" data-testid="longevity-onboarding-cta-title">
                {onboarding?.status === "submitted"
                  ? "Onboarding submitted"
                  : onboarding && onboarding.consent?.agreed
                    ? "Continue your onboarding"
                    : "Start your Longevity Onboarding"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {onboarding?.status === "submitted"
                  ? "You can revisit and edit your responses anytime — our team will re-review updates."
                  : "A guided 10-step intake covering personal context, body, gut, mind, lifestyle and goals."}
              </p>
              <Link to="/portal/longevity/onboarding">
                <Button size="sm" className="rounded-full mt-3" data-testid="longevity-onboarding-cta-btn">
                  {onboarding?.status === "submitted"
                    ? "Review & edit"
                    : onboarding && onboarding.consent?.agreed
                      ? "Continue onboarding"
                      : "Begin onboarding"}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* The Biometric Vault itself — data entry is unchanged */}
        <div data-testid="longevity-vault-section">
          <h2 className="font-editorial text-2xl sm:text-3xl mb-1">Your Biometric Vault</h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
            Tap any card below to add new measurements. Every entry is permanent — each
            save adds a new timestamped record and refreshes the report above.
          </p>
          <BiometricVault
            entries={entries}
            loading={loading}
            onSave={handleSave}
            mode="guest"
          />
        </div>
      </div>
    </div>
  );
}
